import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import api from "../api";
import BottomNavBar from "../landingPageNew/BottomNavBar";
import { ArrowLeft } from "lucide-react";

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px 120px;
  font-family: "Quicksand", sans-serif;
  min-height: 100vh;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #8747d4;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  padding: 0;
  margin-bottom: 24px;

  &:hover {
    opacity: 0.8;
  }
`;

const Header = styled.header`
  margin-bottom: 32px;

  h1 {
    margin: 0 0 8px 0;
    font-size: 28px;
    color: #333;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 15px;
  }
`;

const AnnouncementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AnnouncementCard = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 14px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardIcon = styled.div`
  font-size: 28px;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 2px;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  font-family: "Quicksand", sans-serif;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const CardDate = styled.span`
  font-size: 12px;
  color: #999;
  font-family: "Quicksand", sans-serif;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  font-family: "Quicksand", sans-serif;
  background: ${(props) => props.bg || "#eee"};
  color: ${(props) => props.color || "#666"};
`;

const CardPreview = styled.p`
  margin: 6px 0 0 0;
  font-size: 13px;
  color: #888;
  font-family: "Quicksand", sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #999;
  font-family: "Quicksand", sans-serif;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  font-size: 16px;
  color: #999;
  font-family: "Quicksand", sans-serif;
`;

const SalaamSentList = () => {
  const history = useHistory();
  const adminEmail = localStorage.getItem("userEmail");
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      if (!adminEmail) {
        setIsLoading(false);
        return;
      }
      try {
        const response = await api.get("/api/announcements/admin/all", {
          params: { email: adminEmail },
        });
        setAnnouncements(response.data || []);
      } catch (error) {
        console.error("Failed to fetch announcements:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, [adminEmail]);

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <>
      <Container>
        <BackButton onClick={() => history.push("/announcement-admin")}>
          <ArrowLeft size={16} /> Back to Salaam Admin
        </BackButton>

        <Header>
          <h1>All Salaams Sent</h1>
          <p>{announcements.length} message{announcements.length !== 1 ? "s" : ""} sent</p>
        </Header>

        {isLoading ? (
          <LoadingState>Loading...</LoadingState>
        ) : announcements.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📭</EmptyIcon>
            <EmptyText>No announcements sent yet.</EmptyText>
          </EmptyState>
        ) : (
          <AnnouncementsList>
            {announcements.map((a) => (
              <AnnouncementCard
                key={a._id}
                onClick={() => history.push(`/announcement-admin/sent/${a._id}`)}
              >
                <CardIcon>{a.icon || "📢"}</CardIcon>
                <CardContent>
                  <CardTitle>{a.title}</CardTitle>
                  <CardMeta>
                    <CardDate>
                      {a.createdAt
                        ? new Date(a.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : ""}
                    </CardDate>
                    {a.allowReplies && (
                      <Badge bg="#f0e6ff" color="#8747d4">
                        Replies enabled
                      </Badge>
                    )}
                    {a.replyCount > 0 && (
                      <Badge bg="#e6f7e6" color="#2f8f2f">
                        {a.replyCount} {a.replyCount === 1 ? "reply" : "replies"}
                      </Badge>
                    )}
                    {a.targetEmails && a.targetEmails.length > 0 ? (
                      <Badge bg="#fff3e6" color="#cc7a00">
                        {a.targetEmails.length} targeted
                      </Badge>
                    ) : (
                      <Badge bg="#e8e8e8" color="#666">
                        Broadcast
                      </Badge>
                    )}
                  </CardMeta>
                  <CardPreview>{stripHtml(a.message)}</CardPreview>
                </CardContent>
              </AnnouncementCard>
            ))}
          </AnnouncementsList>
        )}
      </Container>
      <BottomNavBar />
    </>
  );
};

export default SalaamSentList;
